import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { Skeleton } from '@mui/material';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { SidebarTradeCard } from 'screens/Public/UserProfile/context/ProfileTradeSidebarContext';
import {
  useGetTradeAlertsQuery,
  useHideTradeAlertMutation,
  useUnhideTradeAlertMutation,
  TradeAlert,
  TradeAlertsTab,
  TradeAlertsAlertStatus,
  TradeAlertsSort,
} from 'store/services/tradeAlerts/tradeAlertsApi';
import AlertCard from './components/AlertCard';
import ComposeTradeModal, { ComposeTradeRecipient } from './components/ComposeTradeModal/ComposeTradeModal';
import './TradeAlertsPage.css';

const POLL_INTERVAL_MS = 30000;

const SORT_OPTIONS: { value: TradeAlertsSort; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'highest-value', label: 'Highest value' },
  { value: 'lowest-value', label: 'Lowest value' },
];

// Map an alert to the SidebarTradeCard shape the ComposeTradeModal pre-stages.
// The finish is locked to the matched Have item (a specific physical card).
const alertToStagedCard = (alert: TradeAlert): SidebarTradeCard => ({
  id: `alert-${alert.id}`,
  wantListId: alert.wantListItemId,
  cardId: alert.card.id,
  cardName: alert.card.name,
  set: alert.card.setCode,
  setCode: alert.card.setCode,
  condition: alert.card.condition,
  language: alert.card.language,
  foil: alert.card.foil,
  imageUrl: alert.card.imageUrl,
  pointValue: alert.pointValue,
  scryfallId: alert.card.scryfallId,
  acceptFoil: true,
  acceptNonFoil: true,
  availableInFoil: alert.card.foil,
  availableInNonFoil: !alert.card.foil,
  maxQuantity: 1,
});

interface ComposeState {
  recipient: ComposeTradeRecipient;
  stagedCard: SidebarTradeCard;
}

const TradeAlertsPage: React.FC = () => {
  const [tab, setTab] = useState<TradeAlertsTab>('all');
  // Two independent filter axes: alert-status (mutually exclusive) and high-value (composable toggle).
  const [alertStatus, setAlertStatus] = useState<TradeAlertsAlertStatus>('all');
  const [highValue, setHighValue] = useState(false);
  const [sort, setSort] = useState<TradeAlertsSort>('newest');
  const [isPaused, setIsPaused] = useState(false);
  const [compose, setCompose] = useState<ComposeState | null>(null);

  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isFetching, refetch } = useGetTradeAlertsQuery(
    { tab, filter: highValue ? 'high-value' : 'all', sort, alertStatus },
    { pollingInterval: isPaused ? 0 : POLL_INTERVAL_MS },
  );

  const [hideTradeAlert] = useHideTradeAlertMutation();
  const [unhideTradeAlert] = useUnhideTradeAlertMutation();

  // Close sort dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) setIsSortOpen(false);
    };
    if (isSortOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSortOpen]);

  const alerts = data?.alerts ?? [];
  const hiddenCount = data?.hiddenCount ?? 0;
  // The three status pills read their counts from `counts` (reflects tab + high-value,
  // independent of the selected status). Hidden pill uses hiddenCount.
  const counts = data?.counts ?? { all: 0, on: 0, off: 0 };

  const handleResume = () => {
    setIsPaused(false);
    refetch();
  };

  const handleSend = (alert: TradeAlert) => {
    setCompose({
      recipient: { id: alert.requester.id, username: alert.requester.username },
      stagedCard: alertToStagedCard(alert),
    });
  };

  const handleHide = (alert: TradeAlert) => {
    hideTradeAlert({ haveListItemId: alert.haveListItemId, wantListItemId: alert.wantListItemId });
  };

  const handleUnhide = (alert: TradeAlert) => {
    unhideTradeAlert({ haveListItemId: alert.haveListItemId, wantListItemId: alert.wantListItemId });
  };

  const currentSortLabel = SORT_OPTIONS.find(o => o.value === sort)?.label || 'Newest';
  const showEmpty = !isLoading && alerts.length === 0;

  return (
    <>
      <SEOHelmet
        title="Trade Alerts - TradeMagic"
        description="Other traders want these cards and you have them. Send to complete the trade."
        canonical="https://trademagic.gg/trade-alerts"
      />
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg' },
          { name: 'Trade Alerts', url: 'https://trademagic.gg/trade-alerts' },
        ]}
      />

      <div className="trade-alerts-page">
        {/* Header */}
        <div className="trade-alerts-header">
          <h1 className="trade-alerts-title">Trade Alerts</h1>
          <p className="trade-alerts-subtitle">
            Other traders want these cards and you have them. Send to complete the trade.
          </p>
        </div>

        {/* Controls row */}
        <div className="trade-alerts-controls">
          <div className="trade-alerts-controls-left">
            {/* Alert-status axis (mutually exclusive). Independent of the Hidden tab. */}
            <button
              className={`trade-alerts-pill ${alertStatus === 'all' ? 'active' : ''}`}
              onClick={() => setAlertStatus('all')}
            >
              All · {counts.all}
            </button>
            <button
              className={`trade-alerts-pill ${alertStatus === 'on' ? 'active' : ''}`}
              onClick={() => setAlertStatus('on')}
            >
              Alerts on · {counts.on}
            </button>
            <button
              className={`trade-alerts-pill ${alertStatus === 'off' ? 'active' : ''}`}
              onClick={() => setAlertStatus('off')}
            >
              Alerts off · {counts.off}
            </button>
            {/* High-value axis (composable toggle). */}
            <button
              className={`trade-alerts-pill ${highValue ? 'active' : ''}`}
              onClick={() => setHighValue(v => !v)}
            >
              High value
            </button>

            <div className="trade-alerts-sort" ref={sortRef}>
              <button
                type="button"
                className={`trade-alerts-sort-trigger ${isSortOpen ? 'open' : ''}`}
                onClick={() => setIsSortOpen(o => !o)}
              >
                <span>{currentSortLabel}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {isSortOpen && (
                <div className="trade-alerts-sort-menu" role="listbox">
                  {SORT_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className={`trade-alerts-sort-option ${sort === option.value ? 'selected' : ''}`}
                      onClick={() => { setSort(option.value); setIsSortOpen(false); }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="trade-alerts-controls-right">
            <button
              className={`trade-alerts-pill ${tab === 'hidden' ? 'active' : ''}`}
              onClick={() => setTab('hidden')}
            >
              Hidden · {hiddenCount}
            </button>

            <div className={`trade-alerts-live ${isPaused ? 'paused' : ''}`}>
              <span className="trade-alerts-live-dot" />
              <span>{isPaused ? 'Paused' : 'Live'}</span>
            </div>
            <button
              className="trade-alerts-pause"
              onClick={() => (isPaused ? handleResume() : setIsPaused(true))}
            >
              {isPaused ? 'Resume' : 'Pause'}
            </button>
          </div>
        </div>

        {/* Grid / states */}
        {isLoading ? (
          <div className="trade-alerts-grid">
            {[...Array(10)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                sx={{ backgroundColor: 'rgba(255,255,255,0.08)', height: '360px', borderRadius: '12px' }}
              />
            ))}
          </div>
        ) : showEmpty ? (
          <div className="trade-alerts-empty">
            {tab === 'hidden' ? (
              <p>No hidden alerts.</p>
            ) : (
              <p>
                No trade alerts right now. Enable alerts on more cards in your{' '}
                <Link to="/my-cards/have-list">Have List</Link> to catch more opportunities.
              </p>
            )}
          </div>
        ) : (
          <div className={`trade-alerts-grid ${isFetching ? 'is-fetching' : ''}`}>
            {alerts.map(alert => (
              <AlertCard
                key={alert.id}
                alert={alert}
                variant={tab}
                onSend={handleSend}
                onHide={handleHide}
                onUnhide={handleUnhide}
              />
            ))}
          </div>
        )}
      </div>

      {compose && (
        <ComposeTradeModal
          isOpen={true}
          onClose={() => setCompose(null)}
          recipient={compose.recipient}
          initialStagedCard={compose.stagedCard}
        />
      )}
    </>
  );
};

export default TradeAlertsPage;
